import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import ApplicationService from '../../../Services/ApplicationService';
import Field from './Field';
import { parseAmountUnit, useUnitOptions } from './useUnitOptions';
import style from '../../../../scss/components/form.module.scss';

const FALLBACK_CURRENCIES = ['Ft', '€', '$'];

const PriceInput = ({ entity, onChange, ...props }) => {
    const { name } = props;
    const { units: currencies, defaultUnit } = useUnitOptions('price', FALLBACK_CURRENCIES);
    const containerRef = useRef(null);

    const initialParsed = parseAmountUnit(entity[name] || '', FALLBACK_CURRENCIES, FALLBACK_CURRENCIES[0]);
    const [amount, setAmount] = useState(initialParsed.amount);
    const [currency, setCurrency] = useState(initialParsed.currency ?? FALLBACK_CURRENCIES[0]);
    const [suggestions, setSuggestions] = useState([]);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [highlighted, setHighlighted] = useState(-1);
    const debounceRef = useRef(null);

    const prevCurrenciesRef = useRef(FALLBACK_CURRENCIES);
    if (currencies !== prevCurrenciesRef.current) {
        prevCurrenciesRef.current = currencies;
        if (!currencies.includes(currency)) {
            setCurrency(defaultUnit);
        }
    }

    // Sync state when entity value changes externally (e.g., form reset)
    useEffect(() => {
        const parsed = parseAmountUnit(entity[name] || '', FALLBACK_CURRENCIES, FALLBACK_CURRENCIES[0]);
        setAmount(parsed.amount);
        setCurrency(parsed.currency ?? FALLBACK_CURRENCIES[0]);
    }, [entity[name], name]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const emit = useCallback((a, c) => {
        const trimmed = a.trim();
        onChange({ [name]: trimmed ? `${trimmed} ${c}` : '' });
    }, [onChange, name]);

    const loadSuggestions = useCallback((q, c) => {
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            ApplicationService.autocomplete('pcr-values', { type: 'price', q, unit: c })
                .then((values) => {
                    const filtered = values
                        .filter((v) => v.endsWith(` ${c}`))
                        .map((v) => v.slice(0, v.length - c.length - 1));
                    setSuggestions(filtered);
                    setDropdownOpen(filtered.length > 0);
                    setHighlighted(-1);
                })
                .catch(() => {
                    setSuggestions([]);
                    setDropdownOpen(false);
                });
        }, 300);
    }, []);

    const selectSuggestion = useCallback((suggestion) => {
        setAmount(suggestion);
        emit(suggestion, currency);
        setDropdownOpen(false);
        setSuggestions([]);
    }, [emit, currency]);

    const handleAmountChange = useCallback((e) => {
        const val = e.target.value;
        setAmount(val);
        emit(val, currency);
        loadSuggestions(val, currency);
    }, [emit, currency, loadSuggestions]);

    const handleCurrencyChange = useCallback((e) => {
        const c = e.target.value;
        setCurrency(c);
        emit(amount, c);
        loadSuggestions(amount, c);
    }, [emit, amount, loadSuggestions]);

    const handleKeyDown = useCallback((e) => {
        if (!dropdownOpen || suggestions.length === 0) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlighted((prev) => Math.min(prev + 1, suggestions.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlighted((prev) => Math.max(prev - 1, 0));
        } else if (e.key === 'Enter' && highlighted >= 0) {
            e.preventDefault();
            selectSuggestion(suggestions[highlighted]);
        } else if (e.key === 'Escape') {
            setDropdownOpen(false);
        }
    }, [dropdownOpen, suggestions, highlighted, selectSuggestion]);

    return (
        <div className={style.formGroup}>
            <Field {...props}>
                {(hasError, attributes, isChanged) => (
                    <div ref={containerRef} style={{ display: 'flex', gap: '8px', position: 'relative' }}>
                        <div style={{ flex: 1, position: 'relative' }}>
                            <input
                                className={`${style.formTextInput} ${hasError ? style.isInvalid : ''} ${isChanged ? style.changedInput : ''}`}
                                value={amount}
                                onChange={handleAmountChange}
                                onKeyDown={handleKeyDown}
                                onFocus={() => suggestions.length > 0 && setDropdownOpen(true)}
                                autoComplete="off"
                                id={attributes.id}
                                name={attributes.name}
                                placeholder={attributes.placeholder}
                                disabled={attributes.disabled}
                            />
                            {dropdownOpen && suggestions.length > 0 && (
                                <ul style={{
                                    position: 'absolute',
                                    zIndex: 50,
                                    left: 0,
                                    right: 0,
                                    top: '100%',
                                    marginTop: '2px',
                                    background: 'white',
                                    border: '1px solid #b0d4e3',
                                    borderRadius: '4px',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                                    listStyle: 'none',
                                    margin: '2px 0 0',
                                    padding: 0,
                                    maxHeight: '180px',
                                    overflowY: 'auto',
                                }}>
                                    {suggestions.map((s, i) => (
                                        <li key={s}>
                                            <button
                                                type="button"
                                                onMouseDown={(e) => { e.preventDefault(); selectSuggestion(s); }}
                                                style={{
                                                    display: 'block',
                                                    width: '100%',
                                                    textAlign: 'left',
                                                    padding: '6px 10px',
                                                    fontSize: '13px',
                                                    color: '#2d6a84',
                                                    background: highlighted === i ? '#e8f4f8' : 'transparent',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                {s} <span style={{ color: '#9ab8c8' }}>{currency}</span>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        <select
                            className={style.select}
                            value={currency}
                            onChange={handleCurrencyChange}
                            style={{ width: '70px', flexShrink: 0 }}
                        >
                            {currencies.map((c) => (
                                <option key={c} value={c}>{c}</option>
                            ))}
                        </select>
                    </div>
                )}
            </Field>
        </div>
    );
};

export default PriceInput;

PriceInput.propTypes = {
    entity: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
    name: PropTypes.string.isRequired,
    changedFields: PropTypes.array,
};

PriceInput.defaultProps = {
    changedFields: [],
};
